import { useState, useEffect, useContext, useMemo, createContext, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import OSV2NavRail from './OSV2NavRail';
import OSV2TopBar from './OSV2TopBar';
import OSV2Footer from './OSV2Footer';
import type { OSV2Route } from '../OSV2App';
import { useOSTheme } from '../lib/useOSTheme';

interface Props {
  currentRoute: OSV2Route;
  onNavigate: (r: OSV2Route) => void;
  children: ReactNode;
}

// ─────────────────────────────────────────────────────────────────────────
// Focus mode — pages that have their own left sidebar (e.g. the BuildRoom
// lesson player with its lessons rail) flip this flag. The OSV2Shell hides
// its global nav rail while focus mode is on so the page doesn't end up
// with two left sidebars stacked side-by-side.
//
// Skool pattern: top-bar nav + (only on lesson views) a single left lesson
// sidebar + main pane. We mirror that here.
// ─────────────────────────────────────────────────────────────────────────

interface OSChromeContextValue {
  focusMode: boolean;
  setFocusMode: (next: boolean) => void;
}

const OSChromeContext = createContext<OSChromeContextValue | null>(null);

/**
 * `useOSChrome()` — page-level hook to flip focus mode on/off. When `true`,
 * the OS rail hides; the page's own sidebar becomes the only left chrome.
 */
export function useOSChrome(): OSChromeContextValue {
  const ctx = useContext(OSChromeContext);
  if (!ctx) {
    // Allow pages to call the hook outside the shell during tests / Storybook
    // without crashing. Returns a no-op shape.
    return { focusMode: false, setFocusMode: () => {} };
  }
  return ctx;
}

/**
 * OSV2Shell — three-region layout: left rail nav + top bar + main content.
 * Responsive: rail is inline at lg+, slides in as a drawer below lg.
 *
 * Theme: light-by-default, with an OS-only dark-mode opt-in via useOSTheme.
 * The `data-theme` attribute is set on this shell's root container so the
 * scope is the OS only — Marketing + Kickstart never go dark even when the
 * Builder has dark on for the OS.
 */
export default function OSV2Shell({ currentRoute, onNavigate, children }: Props) {
  const [navOpen, setNavOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(() => readStoredNavCollapsed(currentRoute));
  const [focusMode, setFocusMode] = useState(false);
  const { theme, toggle: toggleTheme } = useOSTheme();

  const chromeContextValue = useMemo<OSChromeContextValue>(
    () => ({ focusMode, setFocusMode }),
    [focusMode],
  );

  // Drop focus mode on route change so the page that turned it on can't
  // strand the rail hidden after the Builder navigates away.
  useEffect(() => {
    setFocusMode(false);
  }, [currentRoute]);

  // Close drawer on route change
  useEffect(() => {
    setNavOpen(false);
  }, [currentRoute]);

  // Close drawer on escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <OSChromeContext.Provider value={chromeContextValue}>
    <div
      data-theme={theme === 'dark' ? 'os-dark' : undefined}
      className={cls(
        'grid min-h-[100dvh] grid-cols-1 bg-fd-bg font-geist text-fd-text antialiased',
        focusMode
          ? 'lg:grid-cols-[1fr]'
          : navCollapsed
            ? 'lg:grid-cols-[72px_1fr]'
            : 'lg:grid-cols-[232px_1fr]',
      )}
      style={{
        backgroundImage:
          'linear-gradient(var(--fd-grid) 1px, transparent 1px),' +
          'linear-gradient(90deg, var(--fd-grid) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
        backgroundPosition: '-1px -1px',
      }}
    >
      {/* Inline rail (lg+) — hidden in focus mode so a page-level sidebar can stand alone */}
      {focusMode ? null : (
        <div className="hidden lg:block">
          <OSV2NavRail
            currentRoute={currentRoute}
            onNavigate={onNavigate}
            theme={theme}
            onToggleTheme={toggleTheme}
            collapsed={navCollapsed}
            onToggleCollapsed={() => setNavCollapsed((value) => {
              const next = !value;
              writeStoredNavCollapsed(next);
              return next;
            })}
          />
        </div>
      )}

      {/* Drawer rail (<lg) */}
      <AnimatePresence>
        {navOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={() => setNavOpen(false)}
              className="fixed inset-0 z-40 bg-fd-text/40 backdrop-blur-sm lg:hidden"
              aria-hidden="true"
            />
            <motion.div
              key="drawer"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-y-0 left-0 z-50 w-[280px] shadow-2xl lg:hidden"
              role="dialog"
              aria-label="Navigation"
            >
              <button
                onClick={() => setNavOpen(false)}
                aria-label="Close navigation"
                className="absolute right-2 top-2 z-10 grid h-7 w-7 place-items-center rounded-[6px] text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text"
              >
                <X className="h-4 w-4" />
              </button>
              <OSV2NavRail
                currentRoute={currentRoute}
                onNavigate={onNavigate}
                theme={theme}
                onToggleTheme={toggleTheme}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="flex min-h-0 min-w-0 flex-col">
        <OSV2TopBar
          currentRoute={currentRoute}
          onMenuClick={() => setNavOpen(true)}
        />
        <main className="flex-1 min-h-0">{children}</main>
        <OSV2Footer />
      </div>
    </div>
    </OSChromeContext.Provider>
  );
}

function readStoredNavCollapsed(route: OSV2Route): boolean {
  if (typeof window === 'undefined') return route === 'build-room' || route === 'loop-install';
  try {
    const raw = window.localStorage.getItem('abl:os-nav-collapsed');
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    // Fall back to the lesson-first default below.
  }
  return route === 'build-room' || route === 'loop-install';
}

function writeStoredNavCollapsed(next: boolean) {
  try {
    window.localStorage.setItem('abl:os-nav-collapsed', String(next));
  } catch {
    // In-session collapse still works if persistence is unavailable.
  }
}

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(' ');
}
